import { all, fork, take, put, cancel, select, delay, call } from 'redux-saga/effects';
import { ExternalUserValidationError } from '@ts/utils/errors';
import { set, get, LS_CONTACT_INFO } from '@ts/utils/ls';
import { AnalyticsInstance } from '@ts/utils/analytics';
import {
    trackUpdateCart,
    userUpdate,
    trackPurchase,
    convCartItemsToPurchaseItems,
} from '@ts/utils/iterableUtils';

import api from '@ts/api';
import actions from '../actions';
import selectors from '../selectors';

const isProd = process.env.BUILD_ENV === 'prod';

export function* setContactInfoToLS({ userId, firstName, lastName, phone, secondaryPhone, email }) {
    yield set(
        LS_CONTACT_INFO,
        Object.assign(
            {},
            {
                user_id: userId,
                user_info: {
                    first_name: firstName,
                    last_name: lastName,
                    phone: phone,
                    phone2: secondaryPhone,
                    email: email,
                },
            }
        )
    );
}

export function* onSubmitContactInfo() {
    try {
        yield put(actions.checkout.changeContactInfoIsError(false));
        yield put(actions.checkout.changeContactInfoErrorMessage(''));
        yield put(actions.checkout.changeContactInfoIsLoading(true));
        const contactInfo = yield select(selectors.checkout.makeSelectContactInfo());
        const userId = yield select(selectors.checkout.makeSelectUserId());

        if (!isProd) {
            yield delay(1500); // loading imitation
        }

        const formData = {
            user_id: userId,
            user_info: {
                first_name: contactInfo.firstName,
                last_name: contactInfo.lastName,
                phone: contactInfo.phone,
                phone2: contactInfo.secondaryPhone,
                email: contactInfo.email,
            },
            send_sms: contactInfo.sendSms,
            agreement_confirm: contactInfo.termsConfirmed,
        };

        const userInfo = yield api.checkout.saveUser(formData);

        if (!userInfo.success) {
            const errors = Array.isArray(userInfo.errors) ? userInfo.errors : ['Wrong payload'];
            throw new ExternalUserValidationError(errors.join(', '));
        } else {
            const nextUserId = userInfo.data.user_id;
            yield put(actions.checkout.changeUserId(nextUserId));

            yield call(setContactInfoToLS, {
                userId: nextUserId,
                firstName: contactInfo.firstName,
                lastName: contactInfo.lastName,
                phone: contactInfo.phone,
                secondaryPhone: contactInfo.secondaryPhone,
                email: contactInfo.email,
            });

            yield call(setupUser);

            try {
                const cartItems = yield select(selectors.cart.makeSelectItems());
                trackUpdateCart(
                    convCartItemsToPurchaseItems(
                        cartItems.map((item) => ({
                            ...item,
                            trip_name: item.product_name,
                            activity: { id: item.product_id, slug: item.product_slug },
                        }))
                    )
                );
            } catch {}

            yield put(actions.checkout.changePosition(1));
        }
    } catch (err) {
        yield put(actions.app.error(err));
        yield put(actions.checkout.changeContactInfoIsError(true));
        yield put(actions.checkout.changeContactInfoErrorMessage(err.message));
    } finally {
        yield put(actions.checkout.changeContactInfoIsLoading(false));
    }
}

export function* onSuccessfullyPayment(order) {
    try {
        yield put(actions.checkout.changeOrder(order));
        yield put(actions.checkout.changeOrderDetail(null));
        yield put(actions.checkout.changeOrderIsLoading(true));
        yield put(actions.checkout.changeOrderIsError(false));
        yield put(actions.checkout.changeOrderErrorMessage(''));
        yield put(actions.checkout.changePosition(2));

        if (!isProd) {
            yield delay(1500); // loading imitation
        }

        let resp;

        for (let i = 0; i < 5; i++) {
            try {
                resp = yield api.orders.fetchByAccessKey(order.accesskey);
                break;
            } catch (err) {
                if (i < 4) {
                    yield delay(2000);
                } else {
                    throw err;
                }
            }
        }

        if (!resp.success) {
            const errors = Array.isArray(resp.errors) ? resp.errors : ['Wrong access key'];
            throw new Error(errors.join(', '));
        }

        yield put(actions.checkout.changeOrderDetail(resp.data));
        const items = yield select(selectors.cart.makeSelectItems());

        AnalyticsInstance.wrapAvoidError(() =>
            AnalyticsInstance.checkout.transactionComplete({
                products: AnalyticsInstance.creators.createProductsInCartFromCartItems({
                    categoryName: '',
                })(items),
                purchase: AnalyticsInstance.creators.createPurchase(resp.data),
            })
        );

        const cartData = yield select(selectors.cart.makeSelectData());
        const cartDataItems = Object.values(resp.data.cart_items);

        trackPurchase(
            resp.data.gross_total,
            {
                code: cartData?.discount?.promo?.code || '',
                name: cartData?.discount?.promo?.name || '',
            },
            convCartItemsToPurchaseItems(Array.isArray(cartDataItems) ? cartDataItems : [])
        );
    } catch (err) {
        yield put(actions.app.error(err));
        yield put(actions.checkout.changeOrderIsError(true));
        yield put(actions.checkout.changeOrderErrorMessage(err.message));
    } finally {
        yield put(actions.checkout.changeOrderIsLoading(false));
    }
}

export function* watchSubmitContactInfo() {
    let task;
    while (true) {
        yield take(actions.checkout.ActionTypes.SUBMIT_CONTACT_INFO);
        if (task) {
            yield cancel(task);
            task = undefined;
        }

        task = yield fork(onSubmitContactInfo);
    }
}

export function* watchSuccessfullyPayment() {
    let task;
    while (true) {
        const { order } = yield take(actions.checkout.ActionTypes.SUCCESSFULLY_PAYMENT);
        if (task) {
            yield cancel(task);
            task = undefined;
        }

        task = yield fork(onSuccessfullyPayment, order);
    }
}

export function* setupUser() {
    const userId = yield select(selectors.checkout.makeSelectUserId());
    const contactInfo = yield select(selectors.checkout.makeSelectContactInfo());

    if (userId) {
        AnalyticsInstance.account.userId(userId);
        AnalyticsInstance.account.contactInfo({
            first_name: contactInfo.firstName,
            last_name: contactInfo.lastName,
            phone: contactInfo.phone,
            phone2: contactInfo.secondaryPhone,
            email: contactInfo.email,
            user_id: userId,
            userId: userId,
        });
    }

    if (window._iaq && contactInfo.email) {
        userUpdate(contactInfo.email, {
            firstName: contactInfo.firstName,
            lastName: contactInfo.lastName,
            fullName: [contactInfo.firstName, contactInfo.lastName].filter(Boolean).join(' '),
            phoneNumber: contactInfo.phone,
        });
    }
}

export default function* checkoutSaga() {
    yield all([fork(watchSubmitContactInfo), fork(watchSuccessfullyPayment)]);

    const contactInfo = get(LS_CONTACT_INFO);

    if (contactInfo && typeof contactInfo === 'object') {
        const userId = parseInt(contactInfo.user_id, 10);

        if (userId) {
            yield put(actions.checkout.changeUserId(userId));
        }

        if (contactInfo.user_info && typeof contactInfo.user_info === 'object') {
            const email =
                typeof contactInfo.user_info.email === 'string'
                    ? contactInfo.user_info.email.substring(0, 256)
                    : '';

            const firstName =
                typeof contactInfo.user_info.first_name === 'string'
                    ? contactInfo.user_info.first_name.substring(0, 256)
                    : '';

            const lastName =
                typeof contactInfo.user_info.last_name === 'string'
                    ? contactInfo.user_info.last_name.substring(0, 256)
                    : '';

            const phone =
                typeof contactInfo.user_info.phone === 'string'
                    ? contactInfo.user_info.phone.substring(0, 256)
                    : '';

            const secondaryPhone =
                typeof contactInfo.user_info.phone2 === 'string'
                    ? contactInfo.user_info.phone2.substring(0, 256)
                    : '';

            yield put(
                actions.checkout.changeContactInfo({
                    email,
                    firstName,
                    lastName,
                    phone,
                    secondaryPhone,
                })
            );
        }
    }

    yield call(setupUser);
}
